import { useState } from "react";
import { ModalForm } from "@/components/ModalForm";
import { CompanySearch } from "./CompanySearch";
import { CompanyLogo } from "./CompanyLogo";
import { BasicInformation } from "./BasicInformation";
import { AddressInformation } from "./AddressInformation";
import { NotesField } from "./NotesField";
import { useTranslation } from "react-i18next";

interface AddCompanyFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (company: { 
    name: string; 
    address?: string; 
    assignee?: string; 
    notes?: string;
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    vat_number?: string;
    logo_url?: string;
  }) => Promise<boolean>;
}

export const AddCompanyForm = ({
  isOpen,
  onClose,
  onSubmit
}: AddCompanyFormProps) => {
  const { t } = useTranslation();
  const [newCompany, setNewCompany] = useState({
    name: "",
    street: "",
    postal_code: "",
    city: "",
    country: "Belgium",
    assignee: "",
    notes: "",
    vat_number: "",
    logo_url: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setNewCompany({...newCompany, [id]: value});
  };

  const handleLogoChange = (logoUrl: string | undefined) => {
    setNewCompany({...newCompany, logo_url: logoUrl || ""});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const success = await onSubmit(newCompany);
    if (success) {
      // Reset form and close modal
      setNewCompany({ 
        name: "", 
        street: "", 
        postal_code: "", 
        city: "", 
        country: "Belgium", 
        assignee: "", 
        notes: "",
        vat_number: "",
        logo_url: ""
      });
      onClose();
    }
  };

  const handleCompanyFound = (company: {
    name: string;
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    vat_number?: string;
    logo_url?: string;
  }) => {
    setNewCompany({
      ...newCompany,
      name: company.name,
      street: company.street || "",
      postal_code: company.postal_code || "",
      city: company.city || "",
      country: company.country || "Belgium",
      vat_number: company.vat_number || "",
      logo_url: company.logo_url || ""
    });
  };

  const clearLogoUrl = () => {
    setNewCompany({...newCompany, logo_url: ""});
  };

  return (
    <ModalForm
      title={t("companies.createModal.title")}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={t("companies.createModal.submit")}
      cancelText={t("companies.createModal.cancel")}
      submitDisabled={!newCompany.name.trim()}
    >
      <div className="space-y-5">
        {/* Company Search */}
        <CompanySearch onCompanyFound={handleCompanyFound} />
        
        {/* Logo */}
        <CompanyLogo 
          logoUrl={newCompany.logo_url} 
          companyName={newCompany.name} 
          onClearLogo={clearLogoUrl}
        />
        
        {/* Basic Information */}
        <BasicInformation
          name={newCompany.name}
          vatNumber={newCompany.vat_number}
          assignee={newCompany.assignee}
          logoUrl={newCompany.logo_url}
          onChange={handleChange}
          onLogoChange={handleLogoChange}
        />
        
        {/* Address Information */}
        <AddressInformation
          street={newCompany.street}
          postalCode={newCompany.postal_code}
          city={newCompany.city}
          country={newCompany.country}
          onChange={handleChange}
        />
        
        {/* Additional Information */}
        <NotesField
          notes={newCompany.notes}
          onChange={handleChange}
        />
      </div>
    </ModalForm>
  );
};
